/**
 * gift-card-recipient.js — <gift-card-recipient> custom element
 *
 * Enhances the gift card recipient form:
 * - Toggles the Shopify native "send to recipient" flag as soon as an email
 *   is entered.
 * - Validates the recipient email on submit and on blur, blocking form
 *   submission when invalid.
 * - Updates the 200-character live counter on the message textarea.
 * - Locks the Send-on date to today or later.
 *
 * Dispatches:
 *   gift-card-recipient:invalid — detail: { field, message }
 */

import { Component } from '@theme/component';

// Simple, pragmatic email regex — RFC 5322 is overkill for client-side UX gating.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export class GiftCardRecipient extends Component {
  #flag = null;
  #email = null;
  #name = null;
  #message = null;
  #date = null;
  #counter = null;
  #errorEl = null;
  #form = null;

  onConnect() {
    this.#flag = this.querySelector('[data-gift-card-flag]');
    this.#email = this.querySelector('[data-gift-card-email]');
    this.#name = this.querySelector('[data-gift-card-name]');
    this.#message = this.querySelector('[data-gift-card-message]');
    this.#date = this.querySelector('[data-gift-card-date]');
    this.#counter = this.querySelector('[data-char-counter]');
    this.#errorEl = this.querySelector('[data-gift-card-error]');

    // Lock the send-on date to today or later
    if (this.#date) {
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, '0');
      const dd = String(today.getDate()).padStart(2, '0');
      this.#date.min = `${yyyy}-${mm}-${dd}`;
    }

    if (this.#email) {
      this.on(this.#email, 'input', () => this.#onInput());
      this.on(this.#email, 'blur', () => this.#validate());
    }
    if (this.#name) {
      this.on(this.#name, 'input', () => this.#onInput());
    }
    if (this.#message) {
      this.on(this.#message, 'input', () => this.#updateCounter());
      this.#updateCounter();
    }

    // Hook into the parent form's submit to block invalid submissions
    this.#form = this.closest('form');
    if (this.#form) {
      this.on(this.#form, 'submit', (e) => this.#onSubmit(e));
    }
  }

  #onInput() {
    // Activate the Shopify native flag as soon as there's any recipient data.
    const hasEmail = !!(this.#email && this.#email.value.trim());
    if (this.#flag) {
      this.#flag.value = hasEmail ? 'yes' : '';
    }
    // Clear any previous error while the user types
    this.#clearError();
  }

  #updateCounter() {
    if (!this.#counter || !this.#message) return;
    const len = this.#message.value.length;
    const max = parseInt(this.#message.getAttribute('maxlength') || '200', 10);
    this.#counter.textContent = `${len} / ${max}`;
  }

  #validate() {
    if (!this.#email) return true;
    const value = this.#email.value.trim();

    // If the recipient form is closed (details not open) or empty, no validation needed.
    const details = this.querySelector('details');
    const isOpen = details ? details.open : true;
    if (!isOpen || value.length === 0) {
      this.#clearError();
      return true;
    }

    if (!EMAIL_RE.test(value)) {
      this.#showError("L'email du destinataire n'est pas valide.");
      this.emit('gift-card-recipient:invalid', {
        field: 'email',
        message: "L'email du destinataire n'est pas valide.",
      });
      return false;
    }

    this.#clearError();
    return true;
  }

  #onSubmit(e) {
    // If the gift card flag is set (email filled), the email MUST be valid.
    const hasValue = this.#email && this.#email.value.trim().length > 0;
    if (!hasValue) return; // No recipient opt-in → skip validation
    if (!this.#validate()) {
      e.preventDefault();
      e.stopPropagation();
      this.#email.focus();
    }
  }

  #showError(message) {
    if (this.#errorEl) {
      this.#errorEl.textContent = message;
      this.#errorEl.hidden = false;
    }
    if (this.#email) {
      this.#email.setAttribute('aria-invalid', 'true');
    }
  }

  #clearError() {
    if (this.#errorEl) {
      this.#errorEl.textContent = '';
      this.#errorEl.hidden = true;
    }
    if (this.#email) {
      this.#email.removeAttribute('aria-invalid');
    }
  }
}

if (!customElements.get('gift-card-recipient')) {
  customElements.define('gift-card-recipient', GiftCardRecipient);
}
